import React from 'react'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import * as blueprint from '@adc/blueprint'
// @ts-ignore no types
import { Text } from '@adc/ui-adc'
import type { FeatureFlags } from '@adc/ui-adc'
import { usePropertyContext } from 'common/context/property'
import AuctionDetailsTemplate, {
  RegistrationButton,
  SAVED_MESSAGE,
  UNSAVED_MESSAGE,
  SavePropertyButton,
  similarPropertiesLink,
  MessageContentContainer,
} from '../template'
import styles from '../index.scss'
import hasSimilarProperties from '../../../utils/hasSimilarProperties'
import { shouldShowSealedBidTreatment } from '../../../utils/privateSeller'
import {
  CombinedListingType,
  useListingContext,
} from '../../../context/listing'
import SealedBidStatusMessageBox from './sealedBidStatusMessageBox'
import { getERVRow } from './common'
import { isAcceptedOffer } from '../../../containers/SealedBid/buildingBlocks'

const cx = classnames.bind(styles)

const isSold = (listing: CombinedListingType) =>
  listing.listing_status === graph.enums.ListingStatus.Closed

interface DetailContentProps {
  isSaved: boolean
  shouldShowSealedBidTreatmentForListing: boolean
  listing: CombinedListingType
  isRedesignEnabled?: boolean
}

const detailContent = ({
  isSaved,
  shouldShowSealedBidTreatmentForListing,
  listing,
  isRedesignEnabled,
}: DetailContentProps) => {
  const details = [
    {
      label: (
        <Text color='gray' data-elm-id='post_auction_message'>
          {isSold(listing) && <div>This property has sold.</div>}
          {!shouldShowSealedBidTreatmentForListing && !isSold(listing) && (
            <div>{isSaved ? SAVED_MESSAGE : UNSAVED_MESSAGE}</div>
          )}
        </Text>
      ),
    },
  ]

  const ervRow = getERVRow(listing, isRedesignEnabled)
  ervRow && details.unshift(ervRow)

  return details
}

const soldBanner = (statusLabel: string) => (
  <Text size='tiny' color='white' weight='heavy' className={cx('banner')}>
    {statusLabel}
  </Text>
)

const title = (
  listing: CombinedListingType,
  shouldShowSealedBidTreatmentForListing?: boolean
) => {
  if (shouldShowSealedBidTreatmentForListing) return ''

  return listing.listing_status_label || ''
}

interface AfterProps {
  similarProperties?: any[]
  pushAuthQueueItem: (item: any) => void
  toggleAuthModal: () => void
  featureFlags: FeatureFlags
  user: blueprint.primitives.AuctionUser
  isRedesignEnabled?: boolean
}

const After = ({
  similarProperties,
  pushAuthQueueItem,
  toggleAuthModal,
  featureFlags,
  user,
  isRedesignEnabled,
}: AfterProps) => {
  const { listing, segment } = useListingContext()
  const { property, propertyActions } = usePropertyContext()
  const isSaved = property.is_currently_saved

  const shouldShowSealedBidTreatmentForListing = shouldShowSealedBidTreatment({
    segment,
    featureFlags,
  })

  const currentUserHasNoOffer =
    !segment?.current_user_bidding_status ||
    [
      graph.enums.OnlineAuctionSegmentBiddingStatus.NoBid,
      graph.enums.OnlineAuctionSegmentBiddingStatus.WithdrawnBid,
    ].includes(segment?.current_user_bidding_status)

  let additionalContent = null
  if (shouldShowSealedBidTreatmentForListing) {
    additionalContent = <SealedBidStatusMessageBox />
  } else {
    additionalContent = similarPropertiesLink({
      listing,
      isButton: false,
      hasSimilarProperties: hasSimilarProperties(similarProperties),
    })
  }
  return (
    <AuctionDetailsTemplate
      title={title(listing, shouldShowSealedBidTreatmentForListing)}
      primaryButton={
        // Display the registration button only if the user is allowed to submit new offers,
        // or if is an accepted offer for sealed bids (shows "Go to My Offer").
        currentUserHasNoOffer ||
        (!segment?.__methods__?.allowToSubmitNewOffers() &&
          (!shouldShowSealedBidTreatmentForListing ||
            !isAcceptedOffer(listing))) ? (
          !isSold(listing) && (
            <SavePropertyButton
              listing={listing}
              property={property}
              isSaved={isSaved}
              actions={propertyActions}
              pushAuthQueueItem={pushAuthQueueItem}
              toggleAuthModal={toggleAuthModal}
            />
          )
        ) : (
          <RegistrationButton featureFlags={featureFlags} />
        )
      }
      shouldShowSealedBidTreatment={shouldShowSealedBidTreatmentForListing}
      description={isSold(listing) && soldBanner(listing.listing_status_label)}
      detailContent={detailContent({
        isSaved: Boolean(isSaved),
        shouldShowSealedBidTreatmentForListing,
        listing,
        isRedesignEnabled,
      })}
      additionalContent={additionalContent}
      messageContent={
        <MessageContentContainer
          featureFlags={featureFlags}
          enableBidDepositRequiredMessage
          user={user}
        />
      }
    />
  )
}

export default After
